import {TooltipPlacement} from '@/components/tooltips/types';

export interface Position {
  top: number;
  left: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface Bounds {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export const getElementBounds = (element: Element): Bounds => {
    const rect = element.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
    };
};

export const getViewportBounds = (): Bounds => ({
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
});

export const calculateTooltipPosition = (
    targetElement: Element,
    tooltipDimensions: Dimensions,
    placement: TooltipPlacement = 'top',
    offset: [number, number] = [0, 8]
): { position: Position; finalPlacement: TooltipPlacement } => {
    const targetBounds = getElementBounds(targetElement);
    const viewportBounds = getViewportBounds();
    const [offsetX, offsetY] = offset;

    const positions: Record<TooltipPlacement, Position> = {
        top: {
            left: targetBounds.left + (targetElement.clientWidth / 2) - (tooltipDimensions.width / 2) + offsetX,
            top: targetBounds.top - tooltipDimensions.height - offsetY,
        },
        bottom: {
            left: targetBounds.left + (targetElement.clientWidth / 2) - (tooltipDimensions.width / 2) + offsetX,
            top: targetBounds.bottom + offsetY,
        },
        left: {
            left: targetBounds.left - tooltipDimensions.width - offsetY,
            top: targetBounds.top + (targetElement.clientHeight / 2) - (tooltipDimensions.height / 2) + offsetX,
        },
        right: {
            left: targetBounds.right + offsetY,
            top: targetBounds.top + (targetElement.clientHeight / 2) - (tooltipDimensions.height / 2) + offsetX,
        },
        auto: {left: 0, top: 0}, // Will be calculated below
    };

    const checkFitsInViewport = (pos: Position): boolean => {
        return (
            pos.left >= viewportBounds.left &&
      pos.left + tooltipDimensions.width <= viewportBounds.right &&
      pos.top >= viewportBounds.top &&
      pos.top + tooltipDimensions.height <= viewportBounds.bottom
        );
    };

    if (placement === 'auto') {
        const placements: TooltipPlacement[] = ['top', 'bottom', 'right', 'left'];
    
        for (const testPlacement of placements) {
            const testPosition = positions[testPlacement];
            if (checkFitsInViewport(testPosition)) {
                return {position: testPosition, finalPlacement: testPlacement};
            }
        }
    
        return {position: positions.top, finalPlacement: 'top'};
    }

    const position = positions[placement];
  
    if (checkFitsInViewport(position)) {
        return {position, finalPlacement: placement};
    }

    const fallbackPlacements: Record<TooltipPlacement, TooltipPlacement[]> = {
        top: ['bottom', 'right', 'left'],
        bottom: ['top', 'right', 'left'],
        left: ['right', 'top', 'bottom'],
        right: ['left', 'top', 'bottom'],
        auto: ['top', 'bottom', 'right', 'left'],
    };

    for (const fallbackPlacement of fallbackPlacements[placement]) {
        const fallbackPosition = positions[fallbackPlacement];
        if (checkFitsInViewport(fallbackPosition)) {
            return {position: fallbackPosition, finalPlacement: fallbackPlacement};
        }
    }

    // Constrain tooltip to viewport with some padding
    const padding = 10;
    const constrainedPosition = {
        left: Math.max(
            viewportBounds.left + padding,
            Math.min(position.left, viewportBounds.right - tooltipDimensions.width - padding)
        ),
        top: Math.max(
            viewportBounds.top + padding,
            Math.min(position.top, viewportBounds.bottom - tooltipDimensions.height - padding)
        ),
    };

    return {position: constrainedPosition, finalPlacement: placement};
};
